import BigNumber from 'bignumber.js';
import BN from 'bn.js';
import * as math from 'mathjs';
import { TokenMetadata } from '../services/ft-contract';
import { Pool } from '../services/pool';
import Big from 'big.js';
import _, { sortBy } from 'lodash';
import { WRAP_NEAR_CONTRACT_ID } from '../services/wrap-near';
import { IServerRoute } from '~src/services/smartRouterFromServer';
import { EstimateSwapView } from '../services/swapTypes';

const BPS_CONVERSION = 10000;

const ROUNDING_OFFSETS: BN[] = [];
const BN10 = new BN(10);
for (
  let decimalPlaces = 0, offset = new BN(5);
  decimalPlaces < 24;
  decimalPlaces++, offset = offset.mul(BN10)
) {
  ROUNDING_OFFSETS[decimalPlaces] = offset;
}

export const ONLY_ZEROS = /^0*\.?0*$/;

export const toReadableNumber = (
  decimals: number,
  number: string = '0'
): string => {
  if (!decimals) return number;

  const wholeStr = number.substring(0, number.length - decimals) || '0';
  const fractionStr = number
    .substring(number.length - decimals)
    .padStart(decimals, '0')
    .substring(0, decimals);

  return `${wholeStr}.${fractionStr}`.replace(/\.?0+$/, '');
};

export const toNonDivisibleNumber = (
  decimals: number,
  number: string
): string => {
  if (decimals === null || decimals === undefined) return number;
  const [wholePart, fracPart = ''] = number.split('.');

  return `${wholePart}${fracPart.padEnd(decimals, '0').slice(0, decimals)}`
    .replace(/^0+/, '')
    .padStart(1, '0');
};

export const toPrecision = (
  number: string,
  precision: number,
  withCommas: boolean = false,
  atLeastOne: boolean = true
): string => {
  if (typeof number === 'undefined') return '0';

  const [whole, decimal = ''] = number.split('.');

  let formatted = `${
    withCommas ? formatWithCommas(whole) : whole
  }.${decimal.slice(0, precision)}`.replace(/\.$/, '');
  if (atLeastOne && Number(formatted) === 0 && formatted.length > 1) {
    var lastZeroIndex = formatted.lastIndexOf('0');
    formatted =
      formatted.slice(0, lastZeroIndex) +
      formatted.slice(lastZeroIndex).replace('0', '1');
  }

  return formatted;
};

export const toRoundedReadableNumber = ({
  decimals,
  number,
  precision = 6,
  withCommas = true,
}: {
  decimals: number;
  number?: string;
  precision?: number;
  withCommas?: boolean;
}): string => {
  return toPrecision(toReadableNumber(decimals, number), precision, withCommas);
};

export const convertToPercentDecimal = (percent: number) => {
  return math.divide(percent, 100);
};

export const calculateFeePercent = (fee: number) => {
  return math.divide(fee, 100);
};

export const calculateFeeCharge = (fee: number, total: string) => {
  return math.floor(
    math.evaluate(`(${fee} / ${BPS_CONVERSION}) * ${total}`),
    3
  );
};

export const calculateMarketPrice = (
  pool: Pool,
  tokenIn: TokenMetadata,
  tokenOut: TokenMetadata
) => {
  const cur_in_balance = toReadableNumber(
    tokenIn.decimals,
    pool.supplies[tokenIn.id]
  );

  const cur_out_balance = toReadableNumber(
    tokenOut.decimals,
    pool.supplies[tokenOut.id]
  );

  return math.evaluate(`(${cur_in_balance} / ${cur_out_balance})`);
};

export const calculateExchangeRate = (
  fee: number,
  from: string,
  to: string,
  precision?: number
) => {
  try {
    return math.floor(math.evaluate(`${to} / ${from}`), precision || 4);
  } catch (error) {}
};

const subtraction = (initialValue: string, toBeSubtract: string) => {
  return math.format(math.evaluate(`${initialValue} - ${toBeSubtract}`), {
    notation: 'fixed',
  });
};

export const percentOf = (percent: number, value: number | string) => {
  return math.evaluate(`${convertToPercentDecimal(percent)} * ${value}`);
};

export const percentOfBigNumber = (
  percent: number,
  value: number | string,
  precision: number
) => {
  const valueBig = math.bignumber(value);
  const percentBig = math.bignumber(percent).div(100);

  return toPrecision(
    scientificNotationToString(valueBig.mul(percentBig).toString()),
    precision
  );
};

export const percentLess = (percent: number, value: number | string) => {
  return math.format(math.evaluate(`${value} - ${percentOf(percent, value)}`), {
    notation: 'fixed',
  });
};

export function formatWithCommas(value: string): string {
  const pattern = /(-?\d+)(\d{3})/;
  while (pattern.test(value)) {
    value = value.replace(pattern, '$1,$2');
  }
  return value;
}

export function divide(numerator: string, denominator: string) {
  return math.format(math.evaluate(`${numerator} / ${denominator}`), {
    notation: 'fixed',
  });
}

export function multiply(factor1: string, factor2: string) {
  return math.format(math.evaluate(`${factor1} * ${factor2}`), {
    notation: 'fixed',
  });
}

export const percent = (numerator: string, denominator: string) => {
  if (new Big(denominator || '0').eq(0)) {
    return 0;
  }
  return math.evaluate(`(${numerator} / ${denominator}) * 100`);
};

export const calculateFairShare = ({
  shareOf,
  contribution,
  totalContribution,
}: {
  shareOf: string;
  contribution: string;
  totalContribution: string;
}) => {
  // Amounts are yocto-scale integers; float math loses precision past 2^53.
  return new BigNumber(shareOf)
    .times(contribution)
    .div(totalContribution)
    .toFixed(0, BigNumber.ROUND_HALF_UP);
};

export const toInternationalCurrencySystem = (
  labelValue: string,
  percent?: number
) => {
  const hasPercent = !(percent == undefined || percent == null);
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(percent || 2) + 'B'
    : Math.abs(Number(labelValue)) >= 1.0e6
    ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(percent || 2) + 'M'
    : Math.abs(Number(labelValue)) >= 1.0e3
    ? (Math.abs(Number(labelValue)) / 1.0e3).toFixed(percent || 2) + 'K'
    : Math.abs(Number(labelValue)).toFixed(hasPercent ? percent : 2);
};

export const toInternationalCurrencySystemLongString = (
  labelValue: string,
  percent?: number
) => {
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(percent || 2) + 'B'
    : Math.abs(Number(labelValue)) >= 1.0e6
    ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(percent || 2) + 'M'
    : Math.abs(Number(labelValue)).toFixed(percent || 2);
};

export const toInternationalCurrencySystemNature = (
  labelValue: string,
  percent?: number
) => {
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? new BigNumber(Math.abs(Number(labelValue)) / 1.0e9).toFixed(
        percent || 2,
        1
      ) + 'B'
    : Math.abs(Number(labelValue)) >= 1.0e6
    ? new BigNumber(Math.abs(Number(labelValue)) / 1.0e6).toFixed(
        percent || 2,
        1
      ) + 'M'
    : Math.abs(Number(labelValue)) >= 1.0e3
    ? new BigNumber(Math.abs(Number(labelValue)) / 1.0e3).toFixed(
        percent || 2,
        1
      ) + 'K'
    : niceDecimals(labelValue);
};

export function numberWithCommas(value: number | string) {
  const valueString = typeof value === 'number' ? value.toString() : value;
  const parts = scientificNotationToString(valueString).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

export function scientificNotationToString(strParam: string) {
  let flag = /e/.test(strParam);
  if (!flag) return strParam;

  let hasPositiveExponent = true;
  if (/e-/.test(strParam)) {
    hasPositiveExponent = false;
  }

  const negative = Number(strParam) < 0 ? '-' : '';

  let index = Number(strParam.match(/\d+$/)[0]);

  let basis = strParam.match(/[\d\.]+/)[0];

  const ifFraction = basis.includes('.');

  let wholeStr;
  let fractionStr;

  if (ifFraction) {
    wholeStr = basis.split('.')[0];
    fractionStr = basis.split('.')[1];
  } else {
    wholeStr = basis;
    fractionStr = '';
  }

  if (hasPositiveExponent) {
    if (!ifFraction) {
      return negative + wholeStr.padEnd(index + wholeStr.length, '0');
    } else {
      if (fractionStr.length <= index) {
        return negative + wholeStr + fractionStr.padEnd(index, '0');
      } else {
        return (
          negative +
          wholeStr +
          fractionStr.substring(0, index) +
          '.' +
          fractionStr.substring(index)
        );
      }
    }
  } else {
    if (!ifFraction)
      return (
        negative +
        wholeStr.padStart(index + wholeStr.length, '0').replace(/^0/, '0.')
      );
    else {
      return (
        negative +
        wholeStr.padStart(index + wholeStr.length, '0').replace(/^0/, '0.') +
        fractionStr
      );
    }
  }
}

const niceDecimals = (number: string | number, precision = 2) => {
  const numberString = number.toString();
  const [whole, decimals] = numberString.split('.');
  if (!decimals || Number(decimals) == 0) {
    return whole;
  } else {
    return new BigNumber(number).toFixed(precision, 1);
  }
};

export function separateRoutes(
  actions: EstimateSwapView[],
  outputToken: string
) {
  const routes = [];
  let curRoute = [];

  for (let actionIndex in actions) {
    curRoute.push(actions[actionIndex]);
    if (actions[actionIndex].outputToken === outputToken) {
      routes.push(curRoute);
      curRoute = [];
    }
  }

  return routes;
}

export function getPoolAllocationPercents(pools: Pool[]) {
  if (pools.length === 1) return ['100'];

  if (pools) {
    const partialAmounts = pools.map((pool) => {
      return math.bignumber(pool.partialAmountIn);
    });

    const percents: string[] = new Array(partialAmounts.length).fill('0');

    const sum =
      partialAmounts.length === 1
        ? partialAmounts[0]
        : math.sum(...partialAmounts);

    const sortedAmount = sortBy(partialAmounts, (amount) => Number(amount));

    let minIndexes: number[] = [];

    for (
      let sortedPosition = 0;
      sortedPosition < sortedAmount.length - 1;
      sortedPosition++
    ) {
      let minIndex = -1;

      for (
        let amountIndex = 0;
        amountIndex < partialAmounts.length;
        amountIndex++
      ) {
        if (
          partialAmounts[amountIndex].eq(sortedAmount[sortedPosition]) &&
          !minIndexes.includes(amountIndex)
        ) {
          minIndex = amountIndex;
          minIndexes.push(amountIndex);
          break;
        }
      }
      const roundedPercent = math
        .round(percent(partialAmounts[minIndex].toString(), sum))
        .toString();

      if (Number(roundedPercent) === 0) {
        percents[minIndex] = '1';
      } else {
        percents[minIndex] = roundedPercent;
      }
    }

    const finalPIndex = percents.indexOf('0');

    percents[finalPIndex] = subtraction(
      '100',
      percents.length === 1
        ? Number(percents[0])
        : math.sum(...percents.map((percentText) => Number(percentText)))
    ).toString();

    return percents;
  } else {
    return [];
  }
}
export function getRouteAllocationPercents(routes: IServerRoute[]) {
  if (routes.length === 1) return ['100'];

  if (routes) {
    const partialAmounts = routes.map((route) => {
      return math.bignumber(route.amount_in);
    });

    const percents: string[] = new Array(partialAmounts.length).fill('0');

    const sum =
      partialAmounts.length === 1
        ? partialAmounts[0]
        : math.sum(...partialAmounts);

    const sortedAmount = sortBy(partialAmounts, (amount) => Number(amount));

    const minIndexes: number[] = [];

    for (
      let sortedPosition = 0;
      sortedPosition < sortedAmount.length - 1;
      sortedPosition++
    ) {
      let minIndex = -1;

      for (
        let amountIndex = 0;
        amountIndex < partialAmounts.length;
        amountIndex++
      ) {
        if (
          partialAmounts[amountIndex].eq(sortedAmount[sortedPosition]) &&
          !minIndexes.includes(amountIndex)
        ) {
          minIndex = amountIndex;
          minIndexes.push(amountIndex);
          break;
        }
      }
      const roundedPercent = math
        .round(percent(partialAmounts[minIndex].toString(), sum))
        .toString();

      if (Number(roundedPercent) === 0) {
        percents[minIndex] = '1';
      } else {
        percents[minIndex] = roundedPercent;
      }
    }

    const finalPIndex = percents.indexOf('0');

    percents[finalPIndex] = subtraction(
      '100',
      percents.length === 1
        ? Number(percents[0])
        : math.sum(...percents.map((percentText) => Number(percentText)))
    ).toString();

    return percents;
  } else {
    return [];
  }
}

export function getAllocationsLeastOne(amounts: string[]) {
  if (amounts.length === 0) return [];

  if (amounts.length === 1) return ['100'];

  if (amounts) {
    const partialAmounts = amounts.map((amount) => {
      return math.bignumber(amount);
    });

    const percents: string[] = new Array(partialAmounts.length).fill('0');

    const sum =
      partialAmounts.length === 1
        ? partialAmounts[0]
        : math.sum(...partialAmounts);

    const sortedAmount = sortBy(partialAmounts, (amount) => Number(amount));

    let minIndexes: number[] = [];

    for (
      let sortedPosition = 0;
      sortedPosition < sortedAmount.length - 1;
      sortedPosition++
    ) {
      let minIndex = -1;

      for (
        let amountIndex = 0;
        amountIndex < partialAmounts.length;
        amountIndex++
      ) {
        if (
          partialAmounts[amountIndex].eq(sortedAmount[sortedPosition]) &&
          !minIndexes.includes(amountIndex)
        ) {
          minIndex = amountIndex;
          minIndexes.push(amountIndex);
          break;
        }
      }
      const roundedPercent = math
        .round(percent(partialAmounts[minIndex].toString(), sum))
        .toString();

      if (Number(roundedPercent) === 0) {
        percents[minIndex] = '1';
      } else {
        percents[minIndex] = roundedPercent;
      }
    }

    const finalPIndex = percents.indexOf('0');

    percents[finalPIndex] = subtraction(
      '100',
      percents.length === 1
        ? Number(percents[0])
        : math.sum(...percents.map((percentText) => Number(percentText)))
    ).toString();

    return percents;
  } else {
    return [];
  }
}

export const checkAllocations = (sum: string, allocations: string[]) => {
  if (!allocations || allocations?.length === 0) return [];

  const sumNumber = new Big(sum);
  const sumAllocations = allocations.reduce((total, allocation, index) => {
    return total.plus(new Big(allocation));
  }, new Big(0));

  if (!sumAllocations.eq(sumNumber)) {
    const maxNum = _.maxBy(allocations, (allocation) => Number(allocation));

    const maxIndex = allocations.indexOf(maxNum);

    const leftSum = sumAllocations.minus(maxNum);
    const newMaxNum = sumNumber.minus(leftSum);

    return [
      ...allocations.slice(0, maxIndex),
      newMaxNum.toString(),
      ...allocations.slice(maxIndex + 1),
    ];
  } else return allocations;
};

export const getMaxMin = function (
  id: string,
  max: string,
  token?: TokenMetadata
) {
  let condition;
  if (token) {
    condition = id == WRAP_NEAR_CONTRACT_ID && token.symbol == 'NEAR';
  } else {
    condition = id == WRAP_NEAR_CONTRACT_ID;
  }
  return !condition
    ? max
    : Number(max) <= 0.2
    ? '0'
    : toPrecision(
        scientificNotationToString(new Big(max).minus(0.2).toString()),
        24
      );
};
